'use strict'

import React, { useEffect, useState } from 'react'
import type { ComponentProps } from 'react'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import MemberAvatar from '../member-avatar'
import Circle from '@components/kit/circle'
import { Logomark } from '@components/kit/logo'
import {
  formatCalendarDateInOffset,
  formatMessageTimeInOffset
} from '@utils/date'
import { ChatMessage } from '@store/Chat/types'
import { downloadFile } from './attachment-display'
import FileAttachmentPreview from './file-attachment-preview'
import {
  getMessageBubbleBorderRadius,
  getMessageBubbleStyle,
  resolveBubbleAuthorType,
  resolveBubbleTheme
} from './message-bubble-styles'

type ThemeTextColor = ComponentProps<typeof Text>['textColor']

const MESSAGE_AVATAR_SIZE = 24
const MESSAGE_AVATAR_SLOT_WIDTH = 32
const WALRUS_AI_LOGO_SIZE = 14

function WalrusAIAvatar() {
  return (
    <Circle
      size={MESSAGE_AVATAR_SIZE}
      bg='red'
      alignItems='center'
      justifyContent='center'
    >
      <Logomark
        width={WALRUS_AI_LOGO_SIZE}
        height={WALRUS_AI_LOGO_SIZE}
        fill='white'
      />
    </Circle>
  )
}

function MessageBubbleTime({
  time,
  textColor
}: {
  time: string
  textColor: ThemeTextColor
}) {
  return (
    <Text font='text.12.medium' textColor={textColor} style={{ opacity: 0.8 }}>
      {time}
    </Text>
  )
}

interface RoomMessageProps {
  message: ChatMessage
  userId: number
  showDateBreak?: boolean
  showMemberAvatar?: boolean
  showWalrusAIAvatar?: boolean
  shouldBeSendingMessages?: boolean
  memberTimezoneOffset?: number
  memberDisplayName?: string
  onGetDocumentUrl: (documentId: number) => Promise<string | null>
}

function RoomMessage({
  message,
  userId,
  showDateBreak = false,
  showMemberAvatar = false,
  showWalrusAIAvatar = false,
  shouldBeSendingMessages = true,
  memberTimezoneOffset,
  memberDisplayName,
  onGetDocumentUrl
}: RoomMessageProps) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(0)
  const isSending = (message.isPending ?? false) && !message.sendFailed
  const isSender = message.isSender
  const isWalrusAI = message.walrus_ai ?? false

  useEffect(() => {
    if (message.contentType !== 'FILE' || !message.documentId) {
      return
    }

    onGetDocumentUrl(message.documentId).then(url => {
      if (url) {
        setDownloadUrl(url)
      }
    })
  }, [message.documentId, message.contentType, onGetDocumentUrl])

  useEffect(() => {
    if (!isSender) {
      return
    }

    if (isSending && showSuccessMessage === 0) {
      setShowSuccessMessage(1)
    } else if (!isSending && showSuccessMessage === 1) {
      setShowSuccessMessage(2)
    }
  }, [isSender, isSending, showSuccessMessage])

  useEffect(() => {
    if (showSuccessMessage !== 2) {
      return
    }

    const timeout = setTimeout(() => {
      setShowSuccessMessage(3)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [showSuccessMessage])

  const theme = resolveBubbleTheme(message)
  const authorType = resolveBubbleAuthorType(isSender, isSending)
  const { bg: bubbleBg, textColor } = getMessageBubbleStyle(theme, authorType)
  const formattedTime = formatMessageTimeInOffset(
    message.timestamp,
    memberTimezoneOffset
  )

  const documents = message.documents
  const fileName = documents?.file_name || message.content

  const handleFilePress = async () => {
    if (isDownloading || !message.documentId) {
      return
    }

    setIsDownloading(true)
    try {
      let url = downloadUrl
      if (!url) {
        url = await onGetDocumentUrl(message.documentId)
        if (url) {
          setDownloadUrl(url)
        }
      }
      if (url) {
        await downloadFile(url, fileName)
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const timeAlign = isSender ? 'flex-end' : 'flex-start'

  const messageBody =
    message.contentType === 'FILE' ? (
      <FileAttachmentPreview
        file_name={fileName}
        file_size={documents?.file_size || 0}
        mime_type={documents?.mime_type || ''}
        variant='message'
        textColor={textColor}
        isSender={isSender}
        sendFailed={message.sendFailed}
        isLoading={isDownloading}
        onPress={handleFilePress}
      />
    ) : (
      <Text font='text.14.semibold' textColor={textColor}>
        {message.content}
      </Text>
    )

  const content = (
    <Box flexDirection='column'>
      {messageBody}
      {formattedTime && (
        <Box flexDirection='row' justifyContent={timeAlign} mt='04'>
          <MessageBubbleTime time={formattedTime} textColor={textColor} />
        </Box>
      )}
    </Box>
  )

  return (
    <Box mb={showMemberAvatar || isSender ? '12' : '04'}>
      {showDateBreak && message.timestamp && (
        <Text p='12' variant='fine' textAlign='center'>
          {formatCalendarDateInOffset(message.timestamp, memberTimezoneOffset, {
            includeYear: true,
            includeTime: true
          })}
        </Text>
      )}

      <Box
        flexDirection='row'
        alignItems='flex-end'
        justifyContent={isSender ? 'flex-end' : 'flex-start'}
      >
        {!isSender && (
          <Box width={MESSAGE_AVATAR_SLOT_WIDTH} pr='08'>
            {showMemberAvatar && memberDisplayName && (
              <MemberAvatar
                size={MESSAGE_AVATAR_SIZE}
                name={memberDisplayName}
              />
            )}
          </Box>
        )}

        <Box
          paddingX='16'
          paddingY='12'
          maxWidth='75%'
          bg={bubbleBg}
          style={getMessageBubbleBorderRadius(isSender)}
        >
          {content}
        </Box>

        {isWalrusAI && isSender && (
          <Box width={MESSAGE_AVATAR_SLOT_WIDTH} pl='08'>
            {showWalrusAIAvatar && <WalrusAIAvatar />}
          </Box>
        )}
      </Box>

      {isSender && isSending && shouldBeSendingMessages && (
        <Text font='text.12.medium' textColor='walrusRed800' textAlign='right'>
          ⏳ Sending message...
        </Text>
      )}

      {/* {showLogoutMessage && isSender && isSending && (
        <Text font='text.12.medium' textColor={'walrusRed800'}>
          {
            "⚠️ Your message isn't reaching the server.\nPlease, copy its content to ensure it's not lost.\nTry logging out and back in to fix the issue."
          }
        </Text>
      )} */}

      {message.sendFailed && isSender && (
        <Text font='text.12.medium' textColor='walrusRed800' textAlign='right'>
          {message.errorMessage || 'Failed to send message'}
        </Text>
      )}

      {showSuccessMessage === 2 && isSender && !message.sendFailed && (
        <Text
          font='text.12.medium'
          textColor='walrusGreen500'
          textAlign='right'
        >
          ✔ Message sent
        </Text>
      )}
    </Box>
  )
}

export default RoomMessage
